import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronRight } from 'lucide-react';
import SEO from '../seo/SEO';

const LAST_UPDATED = 'February 2026';

const SECTIONS = [
  { id: 'overview', title: '1. Overview' },
  { id: 'info-we-collect', title: '2. Information We Collect' },
  { id: 'how-we-use', title: '3. How We Use Your Information' },
  { id: 'how-we-share', title: '4. How We Share Information' },
  { id: 'amazon-information', title: '5. Amazon Information & SP-API Data' },
  { id: 'sub-processors', title: '6. Sub-processors' },
  { id: 'cookies', title: '7. Cookies & Tracking Technologies' },
  { id: 'security', title: '8. Data Security' },
  { id: 'retention', title: '9. Data Retention' },
  { id: 'your-rights', title: '10. Your Rights & Choices' },
  { id: 'children', title: '11. Children’s Privacy' },
  { id: 'third-party', title: '12. Third-Party Sites & Services' },
  { id: 'international', title: '13. International Users' },
  { id: 'incident', title: '14. Security Incident Reporting' },
  { id: 'changes', title: '15. Changes to this Policy' },
  { id: 'contact', title: '16. Contact Us' },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="Privacy Policy"
        description="How Shipmaster Fulfillment collects, uses, and protects your data. Last updated February 2026."
        noindex={true}
      />
      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        {/* Header */}
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">Legal</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Privacy Policy
          </h1>
          <p className="mt-3 text-slate-500 text-sm">Last updated: {LAST_UPDATED}</p>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            This Privacy Policy explains how Shipmaster Fulfillment LLC (“Shipmaster,” “we,” “us,” or
            “our”) collects, uses, shares, and protects information when you visit
            shipmasterprep.com, request an estimate, book a consultation, or use our
            third-party logistics (3PL) services.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
          {/* Table of contents */}
          <aside className="lg:sticky lg:top-28 self-start">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
                On this page
              </p>
              <ul className="space-y-1.5">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="flex items-start gap-1.5 text-sm text-slate-700 hover:text-indigo-600 font-medium transition-colors"
                    >
                      <ChevronRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
                      <span>{s.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Body */}
          <article className="prose-legal">
            <Section id="overview" title="1. Overview">
              <p>
                Shipmaster Fulfillment LLC is a Florida-based third-party logistics provider that
                operates from our warehouse in Weston, FL. We work with growing direct-to-consumer
                (D2C) brands, Shopify merchants, and ecommerce sellers across the United States to
                receive, store, pick, pack, and ship inventory. To deliver these services and run
                our website, we collect a limited amount of information about merchants, end
                customers, and visitors.
              </p>
              <p>
                We try to keep this policy plain-English. If anything is unclear, email us at{' '}
                <a href="mailto:info@shipmasterprep.com" className="text-indigo-600 font-semibold hover:underline">
                  info@shipmasterprep.com
                </a>
                .
              </p>
            </Section>

            <Section id="info-we-collect" title="2. Information We Collect">
              <p>We collect three broad categories of information:</p>
              <h3>a. Information you provide</h3>
              <ul>
                <li>Contact details (name, business name, email, phone number, mailing address).</li>
                <li>Website or store URL when you request an estimate.</li>
                <li>Order volume, SKU count, average items per order, special handling needs.</li>
                <li>Booking information collected through our scheduling provider (Calendly).</li>
                <li>Any other information you choose to share in messages, support requests, or onboarding documents.</li>
              </ul>
              <h3>b. Operational data from your store and shipments</h3>
              <ul>
                <li>Inventory records, SKU data, lot numbers, and product descriptions you send us.</li>
                <li>Order data forwarded from your storefront (Shopify, Etsy, WooCommerce, Amazon Seller Central, TikTok Shop, etc.) including end-customer names, ship-to addresses, and order contents.</li>
                <li>Shipping carrier records (UPS, FedEx, USPS Ground), tracking numbers, weight, and dimensions.</li>
              </ul>
              <h3>c. Technical and usage data</h3>
              <ul>
                <li>Device, browser, IP address, referring URL, and pages viewed on shipmasterprep.com.</li>
                <li>Cookies and similar technologies (see Section 5).</li>
                <li>Aggregate analytics gathered through privacy-friendly tools to improve our website.</li>
              </ul>
              <p>
                We do not knowingly collect sensitive personal information (e.g., government IDs,
                health information, biometrics) and ask that you do not transmit it to us.
              </p>
            </Section>

            <Section id="how-we-use" title="3. How We Use Your Information">
              <ul>
                <li>To provide our 3PL services — receiving, storing, picking, packing, and shipping your orders.</li>
                <li>To produce shipping labels and route packages with our carrier partners.</li>
                <li>To respond to estimate requests, schedule consultations, and onboard new clients.</li>
                <li>To send transactional and operational communications (status updates, exception alerts, invoices).</li>
                <li>To improve our website, services, and customer experience.</li>
                <li>To comply with legal obligations, enforce our Terms of Service, and protect Shipmaster, our clients, and end customers from fraud or abuse.</li>
                <li>With your consent, to send marketing and product updates (you can opt out at any time).</li>
              </ul>
              <p>
                We do not sell your personal information, and we do not sell or rent end-customer
                shipping data to advertisers.
              </p>
            </Section>

            <Section id="how-we-share" title="4. How We Share Information">
              <p>
                We share information only with parties that need it to deliver our services or
                that we are legally required to share with:
              </p>
              <ul>
                <li>
                  <strong>Shipping carriers</strong> — UPS, FedEx, USPS, and regional carriers receive ship-to information necessary to deliver each package.
                </li>
                <li>
                  <strong>Ecommerce platforms</strong> — your storefront (Shopify, Etsy, Amazon, etc.) receives tracking and fulfillment status updates so your customers stay informed.
                </li>
                <li>
                  <strong>Service providers</strong> — cloud hosting, warehouse management software, scheduling (Calendly), email delivery, payment processing, and analytics vendors operate under contracts that limit them to the purpose for which we engaged them.
                </li>
                <li>
                  <strong>Legal compliance</strong> — we may disclose information when we have a good-faith belief that disclosure is required by law, subpoena, court order, or to protect rights, safety, or property.
                </li>
                <li>
                  <strong>Business transfers</strong> — if Shipmaster is involved in a merger, acquisition, or asset sale, your information may be transferred subject to this policy.
                </li>
              </ul>
              <p>
                We do not sell personal information to third parties, and we do not share it for
                cross-context behavioral advertising.
              </p>
            </Section>

            <Section id="amazon-information" title="5. Amazon Information & SP-API Data">
              <p>
                When a Client authorizes Shipmaster to access their Amazon Selling Partner account
                via the Selling Partner API (SP-API), or otherwise provides us with information
                originating from Amazon services (collectively, &ldquo;Amazon Information&rdquo;),
                we handle that information in compliance with Amazon&rsquo;s{' '}
                <a
                  href="https://sellercentral.amazon.com/mws/static/policy?documentType=DPP&locale=en_US"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 font-semibold hover:underline"
                >
                  Data Protection Policy (DPP)
                </a>{' '}
                and{' '}
                <a
                  href="https://sellercentral.amazon.com/mws/static/policy?documentType=AUP&locale=en_US"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 font-semibold hover:underline"
                >
                  Acceptable Use Policy (AUP)
                </a>
                , as well as the Solution Provider Agreement and any role-specific Data Processing
                Annexes that apply.
              </p>
              <h3>a. What we may access</h3>
              <ul>
                <li>Order data (Order ID, ship-to address, items, quantities, prices) needed to pick, pack, and ship Client orders.</li>
                <li>Inventory and listing data needed to keep stock counts accurate.</li>
                <li>Shipment, return, and tracking events.</li>
                <li>Limited Personally Identifiable Information (PII) of end customers (name, ship-to address, phone where required by carrier).</li>
              </ul>
              <h3>b. How we protect Amazon Information</h3>
              <ul>
                <li><strong>Encryption in transit.</strong> All transfers of Amazon Information use TLS 1.2 or higher. SP-API requests are made over HTTPS using short-lived OAuth tokens.</li>
                <li><strong>Encryption at rest.</strong> Amazon Information stored in our systems is encrypted at rest using AES-256 (or stronger) on managed databases and object storage.</li>
                <li><strong>Least-privilege access.</strong> Access to Amazon Information is restricted to authorized personnel who need it to perform fulfillment work, enforced via role-based access control, individual accounts, and multi-factor authentication.</li>
                <li><strong>Network controls.</strong> Production systems run in private network segments behind a firewall; remote access requires multi-factor authentication.</li>
                <li><strong>Logging &amp; monitoring.</strong> Access to Amazon Information is logged; logs are retained for at least 90 days and reviewed for anomalies.</li>
                <li><strong>Secure development.</strong> Code changes go through peer review; secrets are stored in a managed secrets vault and rotated regularly. We do not store credentials in source control.</li>
                <li><strong>Vendor management.</strong> Sub-processors that handle Amazon Information are bound by written contracts requiring them to implement security controls at least as protective as ours.</li>
              </ul>
              <h3>c. How we use Amazon Information</h3>
              <p>We use Amazon Information solely to provide fulfillment services to the Client that authorized our access. Specifically, we will not:</p>
              <ul>
                <li>Use Amazon Information for advertising, marketing, or remarketing of any kind.</li>
                <li>Use Amazon Information to compete with Amazon, the Client, or any other selling partner.</li>
                <li>Sell, rent, license, or otherwise commercialize Amazon Information.</li>
                <li>Combine Amazon Information with data from any other source for any purpose other than performing the Services for the Client that supplied it.</li>
                <li>Transfer Amazon Information to any third party that is not an approved sub-processor under this policy.</li>
              </ul>
              <h3>d. Retention &amp; deletion</h3>
              <p>
                We retain Amazon Information only as long as necessary to perform the Services and
                meet legal record-keeping obligations (such as carrier and tax records). On Client
                request, on termination of our agreement, or on revocation of SP-API authorization,
                we will delete or anonymize Amazon Information within 30 days, except for the
                limited records we are required to retain by law or by Amazon&rsquo;s policies. PII
                of end customers is deleted no later than 30 days after the order has shipped,
                unless retention is required for returns, chargebacks, or legal obligations.
              </p>
              <h3>e. Independent assessments</h3>
              <p>
                Shipmaster periodically reviews its security controls and, where required by
                Amazon for our role(s), participates in security assessments and provides
                documentation of compliance with Amazon&rsquo;s DPP. Clients may contact us for
                our current security questionnaire and sub-processor list.
              </p>
            </Section>

            <Section id="sub-processors" title="6. Sub-processors">
              <p>
                We engage a small number of vendors (&ldquo;sub-processors&rdquo;) to help operate
                our website and deliver our services. Each is bound by a written contract that
                limits their use of personal information to the services they provide to us and
                requires appropriate security safeguards.
              </p>
              <ul>
                <li><strong>Cloud hosting &amp; database</strong> — managed cloud infrastructure for our website and operational systems.</li>
                <li><strong>Email delivery</strong> — Resend (transactional email for lead notifications and operational communications).</li>
                <li><strong>Scheduling</strong> — Calendly (consultation booking embed).</li>
                <li><strong>Analytics</strong> — Google Analytics 4 / Google Tag Manager (aggregate site usage; consent-gated). Microsoft Clarity (privacy-friendly heatmaps and session replay; consent-gated; input fields are masked by default and additional sensitive elements are explicitly masked so personal information does not appear in recordings).</li>
                <li><strong>Warehouse management &amp; shipping</strong> — software platforms used to manage inventory, generate labels, and tender shipments.</li>
                <li><strong>Carriers</strong> — UPS, FedEx, USPS and regional carriers (only ship-to information necessary for delivery).</li>
              </ul>
              <p>
                A current list of sub-processors that handle Amazon Information is available on
                request to{' '}
                <a href="mailto:info@shipmasterprep.com" className="text-indigo-600 font-semibold hover:underline">
                  info@shipmasterprep.com
                </a>
                . We will give Clients reasonable advance notice before adding a new sub-processor
                that will handle their Amazon Information.
              </p>
            </Section>

            <Section id="cookies" title="7. Cookies & Tracking Technologies">
              <p>
                We use a small number of cookies and similar technologies to remember preferences,
                measure aggregate site traffic, and identify performance issues. Calendly and other
                embedded tools may set their own cookies when you interact with them. You can
                control cookies through your browser settings; disabling cookies may affect parts
                of the site.
              </p>
            </Section>

            <Section id="security" title="8. Data Security">
              <p>
                We use a layered set of administrative, technical, and physical safeguards
                designed to protect personal information and Amazon Information:
              </p>
              <ul>
                <li><strong>Encryption.</strong> TLS 1.2+ in transit; AES-256 (or stronger) at rest in managed databases and object storage.</li>
                <li><strong>Access control.</strong> Role-based access, individual named accounts, mandatory multi-factor authentication for all administrative access, and the principle of least privilege.</li>
                <li><strong>Credential hygiene.</strong> Secrets and API keys live in a managed secrets vault; we do not commit secrets to source control; tokens (including SP-API tokens) are short-lived and rotated.</li>
                <li><strong>Network security.</strong> Production systems run in private network segments with default-deny firewall rules; remote administrative access requires MFA.</li>
                <li><strong>Vulnerability management.</strong> We patch managed dependencies and operating systems on a regular cadence and review code changes through peer review.</li>
                <li><strong>Logging &amp; monitoring.</strong> System and access logs are retained and reviewed for anomalies.</li>
                <li><strong>Physical security.</strong> Our Weston, FL warehouse uses controlled access, surveillance, and visitor sign-in.</li>
                <li><strong>Vendor security.</strong> Sub-processors that handle personal or Amazon Information are bound by written contracts requiring controls at least as protective as ours.</li>
                <li><strong>Personnel.</strong> Personnel with access to personal information receive privacy and security training and are bound by confidentiality obligations.</li>
              </ul>
              <p>
                No system is 100% secure. We can&rsquo;t guarantee absolute security and ask you
                to use strong passwords and protect your own systems as well.
              </p>
            </Section>

            <Section id="retention" title="9. Data Retention">
              <p>
                We retain personal information only as long as needed for the purposes described
                in this policy, to comply with legal obligations (including tax, accounting, and
                carrier record-keeping), to resolve disputes, and to enforce our agreements. When
                no longer needed, information is securely deleted or anonymized. Specific
                retention rules for Amazon Information are described in Section 5(d).
              </p>
            </Section>

            <Section id="your-rights" title="10. Your Rights & Choices">
              <p>Depending on your location, you may have rights to:</p>
              <ul>
                <li>Access the personal information we hold about you.</li>
                <li>Correct inaccurate information.</li>
                <li>Request deletion (subject to record-keeping obligations).</li>
                <li>Opt out of marketing communications by clicking &ldquo;unsubscribe&rdquo; in any email or contacting us.</li>
                <li>For California residents: rights under the CCPA/CPRA, including the right to know, delete, correct, and limit use of sensitive personal information. Shipmaster does not sell or share personal information for cross-context behavioral advertising.</li>
              </ul>
              <p>
                To exercise any of these rights, email{' '}
                <a href="mailto:info@shipmasterprep.com" className="text-indigo-600 font-semibold hover:underline">
                  info@shipmasterprep.com
                </a>
                . We may need to verify your identity before fulfilling certain requests.
              </p>
            </Section>

            <Section id="children" title="11. Children’s Privacy">
              <p>
                Our website and services are intended for businesses and adults. We do not
                knowingly collect personal information from children under 13 (or under 16 in
                certain jurisdictions). If you believe a child has provided personal information,
                contact us and we will delete it.
              </p>
            </Section>

            <Section id="third-party" title="12. Third-Party Sites & Services">
              <p>
                Our site may link to third-party websites and we embed third-party tools (e.g.,
                Calendly, ecommerce platform integrations). Their privacy practices are governed
                by their own policies; we encourage you to review them.
              </p>
            </Section>

            <Section id="international" title="13. International Users">
              <p>
                Shipmaster operates in the United States. If you access our services from outside
                the U.S., please understand your information will be transferred to and processed
                in the United States, where data-protection laws may differ from those in your
                country.
              </p>
            </Section>

            <Section id="incident" title="14. Security Incident Reporting">
              <p>
                Shipmaster maintains a written incident response plan. If we become aware of a
                confirmed security incident affecting personal information or Amazon Information,
                we will:
              </p>
              <ul>
                <li>Begin investigation and containment without undue delay.</li>
                <li>Notify affected Clients without undue delay, and in any event within 24 hours where the incident affects Amazon Information, providing the information reasonably available about the incident, its impact, and the steps we are taking.</li>
                <li>Notify Amazon and other parties as required by Amazon&rsquo;s DPP and applicable law.</li>
                <li>Cooperate in good faith with Client and Amazon investigations.</li>
              </ul>
              <p>
                Suspected security issues can be reported to{' '}
                <a href="mailto:info@shipmasterprep.com" className="text-indigo-600 font-semibold hover:underline">
                  info@shipmasterprep.com
                </a>{' '}
                with the subject line &ldquo;Security incident.&rdquo;
              </p>
            </Section>

            <Section id="changes" title="15. Changes to this Policy">
              <p>
                We may update this policy from time to time. When we do, we will revise the
                &ldquo;Last updated&rdquo; date above and, where appropriate, notify you by email
                or a notice on our site. Your continued use of our services after the update
                means you accept the revised policy.
              </p>
            </Section>

            <Section id="contact" title="16. Contact Us">
              <p>Questions about this policy or your data? Reach out:</p>
              <p className="!mt-3">
                Shipmaster Fulfillment LLC
                <br />
                Weston, FL
                <br />
                <a href="mailto:info@shipmasterprep.com" className="text-indigo-600 font-semibold hover:underline">
                  info@shipmasterprep.com
                </a>
              </p>
            </Section>

            <div className="mt-12 pt-8 border-t border-slate-200 flex items-center gap-4 text-sm">
              <Link to="/terms" className="text-indigo-600 font-semibold hover:underline">
                View Terms of Service →
              </Link>
              <span className="text-slate-300">·</span>
              <Link to="/" className="text-slate-600 font-medium hover:text-indigo-600">
                Back to home
              </Link>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-28">
      <h2 className="text-2xl font-extrabold text-slate-900 mb-4">{title}</h2>
      <div className="text-slate-700 leading-relaxed space-y-4">{children}</div>
    </section>
  );
}
